import Badge from "./badge";
import ProgressBar from "./progressBar";
import usaStates from "../../usaStates";
import { PiForkKnifeFill } from "react-icons/pi";
import { FaWineGlass } from "react-icons/fa";
import { FaCalendar, FaUsers } from "react-icons/fa";
import { FaGraduationCap } from "react-icons/fa6";

// Static data as fallback
const staticRestaurants = [];

// Generate smooth-ish SVG area and line paths from engagement value
function generateEngagementPaths(
  engagement,
  width = 200,
  height = 80,
  points = 6
) {
  const t = Math.max(0, Math.min(1, (engagement ?? 0) / 100));
  const base = height * 0.6;
  const amplitude = height * 0.35;
  const wave = [0.25, 0.5, -0.1, 0.6, 0.15, 0.35];

  // Build points
  const pts = [];
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * width;
    const w = wave[i % wave.length];
    const y = base - amplitude * (t * w + 0.2);
    pts.push({ x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) });
  }

  // Area path: use quadratic curves between points, then close to bottom
  let areaD = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cx = (pts[i - 1].x + pts[i].x) / 2;
    const cy = (pts[i - 1].y + pts[i].y) / 2;
    areaD += ` Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${pts[i].x} ${pts[i].y}`;
  }
  areaD += ` L ${width} ${height} L 0 ${height} Z`;

  // Line path (top edge): similar but without closing
  let lineD = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cx = (pts[i - 1].x + pts[i].x) / 2;
    const cy = (pts[i - 1].y + pts[i].y) / 2;
    lineD += ` Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${pts[i].x} ${pts[i].y}`;
  }

  return { areaD, lineD };
}

const getInitials = (user) => {
  if (!user) return "?";
  const first = user.first_name?.charAt(0) || "";
  const last = user.last_name?.charAt(0) || "";
  return (first + last).toUpperCase() || "?";
};

const getAvatarColor = (str) => {
  const colors = [
    "bg-red-500",
    "bg-green-500",
    "bg-blue-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
  ];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const Avatar = ({ user, size = 7 }) => {
  const initials = getInitials(user);
  const bgColor = getAvatarColor(user?.uuid || initials);

  return (
    <div
      className={`w-${size} h-${size} rounded-full border-2 border-white overflow-hidden flex items-center justify-center text-xs font-semibold text-white ${bgColor}`}
      title={`${user?.first_name || ""} ${user?.last_name || ""}`}
    >
      {user?.image_url ? (
        <img
          src={process.env.REACT_APP_IMAGE_BASE_URL + user.image_url}
          alt={initials}
          className="w-full h-full object-cover"
        />
      ) : (
        initials
      )}
    </div>
  );
};

export default function RestaurantList({
  restaurants = [],
  activeTab,
  userRole,
  onViewEmployees,
  onManageMenu,
  onLessonProgress,
  onManageRestaurant,
  onEditRestaurant,
}) {
  // Map API data to match the static data structure
  const mappedRestaurants = restaurants.map((restaurant) => ({
    ...restaurant,
    id: restaurant._id || restaurant.uuid, // Ensure we have a consistent ID
    account_owner: restaurant.account_owner,
    status: restaurant.status,
    employees: restaurant.employees?.filter((emp) => emp !== null) || [],
    managers: restaurant.managers,
    dishes: restaurant.current_dishes?.length || 0,
    wines: restaurant.current_wines?.length || 0,
    trainingComplete: restaurant?.dashboard?.lessonCompletionPercentage || 0,
    missedTraining: restaurant?.dashboard?.overDueEmployees || 0,
    engagementRate: restaurant?.dashboard?.engagementRate || 0,
    lastMenuUpdate: restaurant.updatedAt
      ? new Date(restaurant.updatedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "N/A",
  }));

  // Use API data if available, otherwise use static data
  const displayRestaurants =
    mappedRestaurants.length > 0 ? mappedRestaurants : staticRestaurants;

  const filteredRestaurants =
    activeTab === "all"
      ? displayRestaurants
      : displayRestaurants.filter(
        (restaurant) =>
          (activeTab === "active" && restaurant.status === "active") ||
          (activeTab === "inactive" && restaurant.status === "inactive")
      );

  if (filteredRestaurants.length === 0) {
    return (
      <div className="text-center p-8 bg-white rounded-lg border border-gray-200">
        <p className="text-gray-500">No restaurants found</p>
      </div>
    );
  }

  if (userRole === "director" || userRole === "super_admin") {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {filteredRestaurants.map((restaurant) => (
          <div
            key={restaurant.id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 text-left hover:shadow-md transition-shadow"
          >
            {/* Header section (Compact) */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl text-start font-extrabold text-[#881337] mb-0.5">
                  {restaurant.name}
                </h2>
                <p className="text-[11px] text-gray-500 font-medium">
                  {restaurant.address
                    ? `${restaurant.address.street || ""}, ${restaurant.address.city || ""
                    }, ${restaurant.address.state || ""} ${restaurant.address.zip || ""
                    }`
                    : "123 Sunset Boulevard, Los Angeles, CA 90028"}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`px-3 py-1 rounded-full text-[12px] font-bold tracking-wider ${restaurant.status === "active"
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                    }`}
                >
                  {restaurant.status === "active" ? "Active" : "Inactive"}
                </span>
                <div className="flex -space-x-1.5">
                  {[
                    ...(restaurant?.employees || []),
                    ...(restaurant?.managers || []),
                  ].slice(0, 3).map((user) => (
                    <Avatar key={user.uuid} user={user} />
                  ))}
                </div>
              </div>
            </div>

            {/* Training Section (Compact) */}
            <div className="mb-8">
              <h3 className="text-[11px] font-bold text-gray-800 tracking-widest mb-4">
                Training
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {/* Training Completion */}
                <div className="flex flex-col items-center">
                  <div className="relative w-20 h-20 flex items-center justify-center mb-2">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        stroke="currentColor"
                        strokeWidth="6"
                        fill="transparent"
                        className="text-gray-100"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        strokeDasharray={226}
                        strokeDashoffset={
                          226 * (1 - restaurant.trainingComplete / 100)
                        }
                        strokeLinecap="round"
                        className="text-[#881337]"
                      />
                    </svg>
                    <span className="absolute text-sm font-black text-gray-800">
                      {Math.round(restaurant.trainingComplete)}%
                    </span>
                  </div>
                  <p className="text-[12px] font-bold text-gray-800">
                    Completion
                  </p>
                </div>

                {/* Engagement Area Chart */}
                {(() => {
                  const engagementVal = Math.round(
                    restaurant.engagementRate ?? restaurant.engagement ?? 78
                  );
                  const engagementClassLocal =
                    engagementVal >= 75
                      ? "text-emerald-600"
                      : engagementVal >= 50
                        ? "text-amber-500"
                        : "text-red-500";
                  return (
                    <div className="flex flex-col items-center justify-between py-1">
                      <div className="w-full h-12 mb-3 relative">
                        <svg
                          className="w-full h-full"
                          viewBox="0 0 100 40"
                          preserveAspectRatio="none"
                        >
                          <defs>
                            <linearGradient
                              id={`engagement-gradient-${restaurant.id}-dir`}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#881337"
                                stopOpacity="0.15"
                              />
                              <stop
                                offset="100%"
                                stopColor="#881337"
                                stopOpacity="0"
                              />
                            </linearGradient>
                          </defs>
                          {(() => {
                            const { areaD: areaSmall, lineD: lineSmall } =
                              generateEngagementPaths(
                                engagementVal,
                                100,
                                40,
                                6
                              );
                            return (
                              <>
                                <path
                                  d={areaSmall}
                                  fill={`url(#engagement-gradient-${restaurant.id}-dir)`}
                                />
                                <path
                                  d={lineSmall}
                                  fill="none"
                                  stroke="#881337"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  opacity="0.4"
                                />
                              </>
                            );
                          })()}
                        </svg>

                        {/* Centered percentage overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span
                            className={`text-[12px] font-extrabold ${engagementClassLocal}`}
                          >
                            {engagementVal}%
                          </span>
                        </div>
                      </div>
                      <p className="text-[12px] font-bold text-gray-800 text-center">
                        Engagement (30d)
                      </p>
                    </div>
                  );
                })()}

                {/* Warning Status */}
                <div className="flex flex-col items-center">
                  <div className="w-full h-20 bg-[#FFFBEB] rounded-xl border border-[#FEF3C7] flex flex-col items-center justify-center mb-2">
                    <p className="text-[12px] text-[#D97706] font-black">
                      {Math.round(
                        restaurant.employees.length *
                        (restaurant.missedTraining / 100)
                      ) || 0}
                    </p>
                    <p className="text-[7px] text-[#D97706] font-bold uppercase tracking-wider">
                      Overdue
                    </p>
                  </div>
                  <p className="text-[12px] font-bold text-gray-800">Overdue</p>
                </div>
              </div>
            </div>

            {/* Menu Section (Compact Icons) */}
            <div className="mb-6">
              <h3 className="text-[12px] font-bold text-gray-800 tracking-widest mb-4">
                Menu
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50/50 rounded-xl p-3 flex flex-col items-center border border-transparent hover:border-gray-100 transition-all">
                  <span className="text-sm font-black text-gray-800 mb-1">
                    {restaurant.dishes}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400 uppercase">
                    Dishes
                  </span>
                </div>
                <div className="bg-gray-50/50 rounded-xl p-3 flex flex-col items-center border border-transparent hover:border-gray-100 transition-all">
                  <span className="text-sm font-black text-gray-800 mb-1">
                    {restaurant.wines}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400 uppercase">
                    Wines
                  </span>
                </div>
                <div className="bg-gray-50/50 rounded-xl p-3 flex flex-col items-center border border-transparent hover:border-gray-100 transition-all">
                  <span className="text-[12px] font-black text-gray-800 text-center truncate w-full px-1 mb-1">
                    {restaurant.lastMenuUpdate}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400 uppercase">
                    Last Update
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons (Compact Grid) */}
            <div className="grid grid-cols-2 gap-2 pt-4 border-t border-gray-50">
              <button
                onClick={() => onViewEmployees(restaurant)}
                className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 border-[#881337] text-[#881337] hover:bg-[#881337] hover:text-white transition-all text-[12px] font-black"
              >
                <FaUsers className="w-3 h-3" /> Staff
              </button>
              <button
                onClick={() => onLessonProgress(restaurant)}
                className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 border-[#881337] text-[#881337] hover:bg-[#881337] hover:text-white transition-all text-[12px] font-black"
              >
                <FaGraduationCap className="w-3 h-3" /> Lessons
              </button>
              <button
                onClick={() => onManageMenu(restaurant)}
                className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 border-[#881337] text-[#881337] hover:bg-[#881337] hover:text-white transition-all text-[12px] font-black"
              >
                <PiForkKnifeFill className="w-3 h-3" /> Menu
              </button>
              <button
                onClick={() => onManageRestaurant(restaurant)}
                className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 border-[#881337] text-[#881337] hover:bg-[#881337] hover:text-white transition-all text-[12px] font-black"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>{" "}
                Manage
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (userRole === "manager") {
    return (
      <div className="space-y-10">
        {filteredRestaurants.map((restaurant) => (
          <div
            key={restaurant.id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-8 text-left"
          >
            {/* Header section */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-2xl font-extrabold text-[#881337] mb-1">
                  {restaurant.name}
                </h2>
                <p className="text-sm text-gray-500 font-medium">
                  {restaurant.address
                    ? `${restaurant.address.street || ""}, ${restaurant.address.city || ""
                    }, ${restaurant.address.state || ""} ${restaurant.address.zip || ""
                    }`
                    : "123 Sunset Boulevard, Los Angeles, CA 90028"}
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-[11px] font-bold  tracking-wider ${restaurant.status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                      }`}
                  >
                    {restaurant.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 font-bold ">
                    Managers
                  </span>
                  <div className="flex -space-x-1.5">
                    {[
                      ...(restaurant?.employees || [])
                    ].slice(0, 3).map((user) => (
                      <Avatar key={user.uuid} user={user} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Training Overview Section */}
            <div className="mb-10">
              <h3 className="text-sm font-bold text-gray-800  tracking-widest mb-6">
                Training Overview
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Training Completion */}
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32 flex items-center justify-center mb-4">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="58"
                        stroke="currentColor"
                        strokeWidth="10"
                        fill="transparent"
                        className="text-gray-100"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="58"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="transparent"
                        strokeDasharray={364.4}
                        strokeDashoffset={
                          364.4 * (1 - restaurant.trainingComplete / 100)
                        }
                        strokeLinecap="round"
                        className="text-[#881337]"
                      />
                    </svg>
                    <span className="absolute text-2xl font-black text-gray-800">
                      {Math.round(restaurant.trainingComplete)}%
                    </span>
                  </div>
                  <p className="text-[13px] font-bold text-gray-800">
                    Training Completion
                  </p>
                  <p className="text-[11px] text-gray-400 font-medium">
                    {Math.round(restaurant.trainingComplete)}% Complete
                  </p>
                </div>

                {/* Engagement Area Chart (SVG Placeholder) */}
                <div className="flex flex-col items-center justify-between">
                  {/* <div className="w-full h-24 mb-6 relative">
                    <svg
                      className="w-full h-full"
                      viewBox="0 0 200 80"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id="engagement-gradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#881337"
                            stopOpacity="0.2"
                          />
                          <stop
                            offset="100%"
                            stopColor="#881337"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,40 C30,35 60,38 90,30 C120,22 150,25 200,28 L200,80 L0,80 Z"
                        fill="url(#engagement-gradient)"
                      />
                      <path
                        d="M0,40 C30,35 60,38 90,30 C120,22 150,25 200,28"
                        fill="none"
                        stroke="#881337"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        opacity="0.4"
                      />
                    </svg>
                  </div> */}
                  {(() => {
                    const engagementVal = Math.round(
                      restaurant.engagementRate ?? restaurant.engagement ?? 78
                    );
                    const engagementClassLocal =
                      engagementVal >= 75
                        ? "text-emerald-600"
                        : engagementVal >= 50
                          ? "text-amber-500"
                          : "text-red-500";
                    return (
                      <div className="text-center">
                        <div className="w-full h-24 mb-6 relative">
                          <svg
                            className="w-full h-full"
                            viewBox="0 0 200 80"
                            preserveAspectRatio="none"
                          >
                            <defs>
                              <linearGradient
                                id={`engagement-gradient-${restaurant.id}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#881337"
                                  stopOpacity="0.2"
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#881337"
                                  stopOpacity="0"
                                />
                              </linearGradient>
                            </defs>
                            {(() => {
                              const { areaD, lineD } = generateEngagementPaths(
                                engagementVal,
                                200,
                                80,
                                6
                              );
                              return (
                                <>
                                  <path
                                    d={areaD}
                                    fill={`url(#engagement-gradient-${restaurant.id})`}
                                  />
                                  <path
                                    d={lineD}
                                    fill="none"
                                    stroke="#881337"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    opacity="0.4"
                                  />
                                </>
                              );
                            })()}
                          </svg>

                          {/* Centered percentage overlay */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span
                              className={`text-[14px] font-extrabold ${engagementClassLocal}`}
                            >
                              {engagementVal}%
                            </span>
                          </div>
                        </div>

                        <p className="text-[13px] font-bold text-gray-800">
                          Engagement
                        </p>
                        <p
                          className={`text-[11px] font-medium ${engagementClassLocal}`}
                        >
                          {engagementVal}% Average
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Warning Status Card */}
                <div className="flex flex-col items-center">
                  <div className="w-full h-32 bg-[#FFFBEB] rounded-2xl border border-[#FEF3C7] flex flex-col items-center justify-center mb-4">
                    <p className="text-[12px] text-[#D97706] font-extrabold  tracking-widest mb-1">
                      Overdue
                    </p>
                    <p className="text-4xl font-black text-[#881337] mb-1">
                      {Math.round(
                        restaurant.employees.length *
                        (restaurant.missedTraining / 100)
                      ) || 2}
                    </p>
                    <p className="text-[12px] text-[#D97706] font-bold">
                      Training Items
                    </p>
                  </div>
                  <p className="text-[13px] font-bold text-gray-800">
                    Warning Status
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Overview Section */}
            <div className="mb-8">
              <h3 className="text-sm font-bold text-gray-800  tracking-widest mb-6">
                Menu Overview
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50/50 rounded-2xl p-8 flex flex-col items-center group hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <div className="p-3 bg-pink-50 rounded-full mb-4 text-[#881337]">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M9 11h6"/><path d="M12 8v6"/></svg> */}
                    <PiForkKnifeFill className="w-5 h-5" />
                  </div>
                  <span className="text-3xl font-black text-gray-800 mb-1">
                    {restaurant.dishes}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400  tracking-wider">
                    Total Dishes
                  </span>
                </div>
                <div className="bg-slate-50 rounded-2xl p-8 flex flex-col items-center group hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <div className="p-3 bg-pink-50 rounded-full mb-4 text-[#881337]">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3z"/></svg> */}
                    <FaWineGlass className="w-5 h-5" />
                  </div>
                  <span className="text-3xl font-black text-gray-800 mb-1">
                    {restaurant.wines}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400  tracking-wider">
                    Total Wines
                  </span>
                </div>
                <div className="bg-slate-50 rounded-2xl p-8 flex flex-col items-center group hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <div className="p-3 bg-orange-50 rounded-full mb-4 text-orange-500">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> */}
                    <FaCalendar className="w-5 h-5" />
                  </div>
                  <span className="text-xl font-black text-gray-800 mb-1">
                    {restaurant.lastMenuUpdate}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400  tracking-wider">
                    Last Menu Update
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom mini stats section */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gray-50/50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[12px] text-gray-400 font-bold  tracking-wider mb-0.5">
                    Total Staff
                  </p>
                  <p className="text-lg font-black text-gray-800">
                    {restaurant.employees.length}
                  </p>
                </div>
                {/* <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#881337" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-30"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> */}
                <FaUsers className="w-8 h-8 text-[#881337] opacity-30" />
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[12px] text-gray-400 font-bold  tracking-wider mb-0.5">
                    Active Learners
                  </p>
                  <p className="text-lg font-black text-gray-800">
                    {Math.round(restaurant.employees.length * 0.85)}
                  </p>
                </div>
                {/* <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-30"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> */}
                <FaGraduationCap className="w-8 h-8 text-[#10b981] opacity-30" />
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[12px] text-gray-400 font-bold  tracking-wider mb-0.5">
                    Menu Categories
                  </p>
                  <p className="text-lg font-black text-gray-800">8</p>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#881337"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-30"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[12px] text-gray-400 font-bold  tracking-wider mb-0.5">
                    Wine Regions
                  </p>
                  <p className="text-lg font-black text-gray-800">12</p>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-30"
                >
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                  <line x1="8" y1="2" x2="8" y2="18" />
                  <line x1="16" y1="6" x2="16" y2="22" />
                </svg>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-50">
              <button
                onClick={() => onViewEmployees(restaurant)}
                className="group flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-[#881337] transition-all hover:bg-[#881337] hover:shadow-lg active:scale-95"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#881337] group-hover:text-white transition-colors"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span className="text-[12px] font-black text-[#881337] group-hover:text-white  transition-colors">
                  View Employees
                </span>
              </button>
              <button
                onClick={() => onLessonProgress(restaurant)}
                className="group flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-[#881337] transition-all hover:bg-[#881337] hover:shadow-lg active:scale-95"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#881337] group-hover:text-white transition-colors"
                >
                  <circle cx="12" cy="8" r="6" />
                  <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
                </svg>
                <span className="text-[12px] font-black text-[#881337] group-hover:text-white  transition-colors">
                  Lesson Progress
                </span>
              </button>
              <button
                onClick={() => onManageMenu(restaurant)}
                className="group flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-[#881337] transition-all hover:bg-[#881337] hover:shadow-lg active:scale-95"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#881337] group-hover:text-white transition-colors"
                >
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                <span className="text-[12px] font-black text-[#881337] group-hover:text-white  transition-colors">
                  Manage Menu
                </span>
              </button>
              <button
                onClick={() => onManageRestaurant(restaurant)}
                className="group flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-[#881337] transition-all hover:bg-[#881337] hover:shadow-lg active:scale-95"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#881337] group-hover:text-white transition-colors"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <span className="text-[12px] font-black text-[#881337] group-hover:text-white  transition-colors">
                  Manage Restaurant
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {filteredRestaurants.map((restaurant) => (
        <div
          key={restaurant._id}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          <div className="p-4 border-b border-gray-100">
            <div className="flex justify-between items-start">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-textcolor">
                  {restaurant.name}
                </h3>
                <p>
                  {restaurant.address
                    ? `${restaurant.address.street || ""}, ${restaurant.address.city || ""
                    }, ${restaurant.address.state
                      ? usaStates[restaurant.address.state] ||
                      restaurant.address.state
                      : ""
                    } ${restaurant.address.zip || ""}`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    onEditRestaurant(
                      restaurants.find((r) => r._id === restaurant._id)
                    )
                  }
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    <path d="m15 5 4 4" />
                  </svg>
                </button>
                {restaurant.status === "active" ? (
                  <Badge className="bg-iconbackground text-gray-900 border-iconbackground">
                    Active
                  </Badge>
                ) : (
                  <Badge
                    variant="default"
                    className="bg-gray-50 text-gray-500 border-gray-200"
                  >
                    Deactivated
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 divide-x divide-gray-100">
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Employees</p>
              <p className="text-2xl font-semibold">
                {restaurant.employees.length}
              </p>
            </div>
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Total Dishes</p>
              <p className="text-2xl font-semibold">{restaurant.Totaldishes}</p>
            </div>
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Total Wines</p>
              <p className="text-2xl font-semibold">{restaurant.wines}</p>
            </div>
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Training Complete</p>
              <p className="text-2xl font-semibold">
                {Math.round(restaurant.trainingComplete)}%
              </p>
            </div>
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Missed Training</p>
              <p className="text-2xl font-semibold">
                {Math.round(restaurant.missedTraining)}%
              </p>
            </div>
            <div className="p-4 bg-background bg-opacity-40 m-2.5 text-left">
              <p className="text-sm text-textcolor mb-1">Last Menu Update</p>
              <p className="text-2xl font-semibold">
                {restaurant.lastMenuUpdate}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 divide-x">
            <button
              onClick={() => onViewEmployees(restaurant)}
              className="p-4 text-center hover:bg-gray-50 transition-colors flex justify-center items-center gap-3 border border-gray-300 rounded-xl m-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="text-sm font-medium">View Employees</span>
            </button>
            <button
              onClick={() => onManageMenu(restaurant)}
              className="p-4 text-center hover:bg-gray-50 transition-colors flex justify-center items-center gap-3 border border-gray-300 rounded-xl m-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
              <span className="text-sm font-medium">Manage Menu</span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 divide-x ">
            <button
              onClick={() => onLessonProgress(restaurant)}
              className="p-4 text-center hover:bg-gray-50 transition-colors flex justify-center items-center gap-3 border border-gray-300 rounded-xl m-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="8" r="6" />
                <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
              </svg>
              <span className="text-sm font-medium">Lesson Progress</span>
            </button>
            <button
              onClick={() => onManageRestaurant(restaurant)}
              className="p-4 text-center hover:bg-gray-50 transition-colors flex justify-center items-center gap-3 border border-gray-300 rounded-xl m-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="20" height="14" x="2" y="5" rx="2" />
                <line x1="2" x2="22" y1="10" y2="10" />
              </svg>
              <span className="text-sm font-medium">Manage Restaurant</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
